<template>
  <button
    :type="type"
    :disabled="disabled"
    :class="buttonClasses"
    @click="handleClick"
    :aria-label="ariaLabel"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'primary',
  disabled: false,
  type: 'button',
  ariaLabel: ''
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClasses = computed(() => {
  const baseClasses = 'icon-btn';
  const variantClass = `icon-btn-${props.variant}`;
  const sizeClass = `icon-btn-${props.size}`;
  const disabledClass = props.disabled ? 'opacity-50 cursor-not-allowed' : '';

  return [baseClasses, variantClass, sizeClass, disabledClass].join(' ');
});

function handleClick(event: MouseEvent) {
  if (!props.disabled) {
    emit('click', event);
  }
}
</script>

<style scoped lang="scss">
.icon-btn {
  @apply inline-flex items-center justify-center rounded-full transition-all duration-300;
  @apply focus:outline-none focus:ring-2 focus:ring-offset-2;
  
  position: relative;
  z-index: 999999999999;

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}

.icon-btn-primary {
  @apply bg-green-500 text-white;
  @apply hover:bg-green-600;
  @apply focus:ring-green-500;
  background: $spotify-green;
  
  &:hover:not(:disabled) {
    background: darken($spotify-green, 8%);
    box-shadow: $shadow-lg;
  }
}

.icon-btn-secondary {
  @apply bg-gray-700 text-white;
  @apply hover:bg-gray-600;
  @apply focus:ring-gray-500;
}

.icon-btn-ghost {
  @apply bg-gray-800 bg-opacity-50 text-white;
  @apply hover:bg-opacity-70;
  @apply focus:ring-gray-500;
}

.icon-btn-sm {
  @apply w-10 h-10;
  font-size: 1rem;
}

.icon-btn-md {
  @apply w-14 h-14;
  font-size: 1.5rem;
}

.icon-btn-lg {
  @apply w-20 h-20;
  font-size: 2rem;
}

.icon-btn-xl {
  @apply w-24 h-24;
  font-size: 2.5rem;
}
</style>